import React, { useMemo, useState } from 'react';
import { Paper, Typography, Stack, FormControl, Select, MenuItem, Button, Box } from '@mui/material';
import Chart from 'react-apexcharts';
import { exportCsvWithDate } from './csvUtil';

const GRAPH_HEIGHT = 300;

function computeBins(values, binCount = 20) {
  const numeric = values.filter(v => typeof v === 'number' && !isNaN(v));
  if (numeric.length === 0) return { bins: [], counts: [] };
  const min = Math.min(...numeric);
  const max = Math.max(...numeric);
  const range = max - min || 1;
  const binSize = range / binCount;
  const counts = new Array(binCount).fill(0);
  numeric.forEach(v => {
    let idx = Math.floor((v - min) / binSize);
    if (idx < 0) idx = 0;
    if (idx >= binCount) idx = binCount - 1;
    counts[idx]++;
  });
  const bins = counts.map((c, i) => {
    const a = min + i * binSize;
    const b = a + binSize;
    return `${a.toFixed(1)}–${b.toFixed(1)}`;
  });
  return { bins, counts };
}

const DistributionHistogram = ({ adjustedTeamsMap = {} }) => {
  const [valueType, setValueType] = useState('adjusted');
  const rows = useMemo(() => Object.values(adjustedTeamsMap || {}), [adjustedTeamsMap]);
  const values = useMemo(() => rows.map(r => Number(valueType === 'adjusted' ? r.adjustedTotal : r.originalTotal) || 0), [rows, valueType]);

  const { bins, counts } = useMemo(() => computeBins(values, 20), [values]);

  const options = useMemo(() => ({
    chart: { id: 'histogram', toolbar: { show: true }, animations: { enabled: false, dynamicAnimation: { enabled: false } } },
    plotOptions: { bar: { horizontal: false, columnWidth: '70%' } },
    xaxis: { categories: bins, labels: { rotate: -45 } },
    yaxis: { title: { text: 'Count' } },
    dataLabels: { enabled: false },
    // show per-bar tooltip but follow cursor and append to body to avoid positioning bugs
    tooltip: {
      enabled: true,
      shared: false,
      intersect: true,
      followCursor: false,
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const labels = (w.config.xaxis && w.config.xaxis.categories) || [];
        const binLabel = labels[dataPointIndex] || '';
        const val = series[seriesIndex] && series[seriesIndex][dataPointIndex] !== undefined ? series[seriesIndex][dataPointIndex] : 0;
        return `<div style="pointer-events:none;padding:8px;font-size:13px;"><strong>${escapeHtml(binLabel)}</strong><div style="margin-top:6px">Count: <strong>${val}</strong></div></div>`;
      }
    },
    // disable hover visual state transitions which can cause flicker
    states: { hover: { filter: { type: 'none' } } },
    fill: { opacity: 1, opacityOnHover: 1 }
  }), [bins]);

  const handleExport = () => {
    const headers = ['bin','count'];
    const rowsCsv = bins.map((b, i) => ({ bin: b, count: counts[i] }));
    exportCsvWithDate('distribution-histogram', headers, rowsCsv);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">Score Distribution</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small">
            <Select value={valueType} onChange={(e) => setValueType(e.target.value)}>
              <MenuItem value="adjusted">Adjusted Total</MenuItem>
              <MenuItem value="original">Original Total</MenuItem>
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={handleExport}>Export CSV</Button>
        </Stack>
      </Stack>

      <Box sx={{ height: GRAPH_HEIGHT, mt: 2 }}>
        <Chart type="bar" series={[{ name: 'Count', data: counts }]} options={options} height={GRAPH_HEIGHT} />
      </Box>
    </Paper>
  );
};

function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export default DistributionHistogram;
